import { SasTokenPrefix, SasTokenPrivilege } from "./user/types";
import { getSasToken } from "./userApi";

interface UploadFileToAzureParams {
  blobUrl: string;
  sasToken: string;
  file: File;
  certificateType: string;
}

const BLOCK_BLOB_HEADER = "BlockBlob";

function buildAzureUploadUrl(blobUrl: string, sasToken: string): string {
  if (!sasToken) return blobUrl;
  const trimmedToken = sasToken.startsWith("?") ? sasToken.slice(1) : sasToken;
  if (!trimmedToken) return blobUrl;
  const separator = blobUrl.includes("?") ? "&" : "?";
  return `${blobUrl}${separator}${trimmedToken}`;
}

export async function uploadFileToAzureBlob({
  blobUrl,
  sasToken,
  file,
  certificateType,
}: UploadFileToAzureParams): Promise<{ documentUrl: string; certificateType: string }[]> {
  const targetUrl = buildAzureUploadUrl(blobUrl, sasToken);
  const response = await fetch(targetUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": BLOCK_BLOB_HEADER,
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (response.status !== 200 && response.status !== 201) {
    throw new Error(
      `Azure blob upload failed with status ${response.status}${
        response.statusText ? ` (${response.statusText})` : ""
      }`,
    );
  }

  return [
    {
      documentUrl: blobUrl,
      certificateType,
    },
  ];
}

export async function getDocumentsWithToken(
  documentUrl: string,
  prefix: SasTokenPrefix,
): Promise<Blob> {
  if (!documentUrl || !/^https?:\/\//.test(documentUrl)) {
    throw new Error("Invalid URL passed to getDocumentsWithToken");
  }

  const payload = {
    prefix,
    contentType: null,
    privilege: SasTokenPrivilege.READ,
    blobUrl: documentUrl,
  };

  const { sasToken } = await getSasToken(payload);

  const trimmedToken = sasToken.startsWith("?") ? sasToken.slice(1) : sasToken;
  const targetUrl =
    trimmedToken && trimmedToken.length > 0
      ? `${documentUrl}${documentUrl.includes("?") ? "&" : "?"}${trimmedToken}`
      : documentUrl;

  const response = await fetch(targetUrl, {
    method: "GET",
    headers: {
      "x-ms-blob-type": BLOCK_BLOB_HEADER,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to retrieve document. Status: ${response.status}`);
  }

  return await response.blob();
}
